'use client';

import { useState, type ReactNode } from 'react';
import {
  useFloating,
  useHover,
  useInteractions,
  offset,
  shift,
  flip,
  autoUpdate,
  FloatingPortal,
} from '@floating-ui/react';

type TooltipProps = {
  label: ReactNode;
  children: ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  disabled?: boolean;
};

export function CustomTooltip({
  label,
  children,
  placement = 'top',
  disabled = false,
}: TooltipProps) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  const hover = useHover(context, {
    move: false,
    enabled: !disabled,
    delay: { open: 50, close: 100 },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className="inline-block"
      >
        {children}
      </span>

      {label ? (
        <FloatingPortal>
          {open && (
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              role="tooltip"
              className="z-50 max-w-[330px] text-start rounded-md bg-white px-2 py-3 text-xs text-black/40 shadow-lg"
            >
              {label}
            </div>
          )}
        </FloatingPortal>
      ) : null}
    </>
  );
}
